import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/auth.context";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getDefaultAvatar } from "@/utils/getDefaultAvatar";
import { Mail, Phone, MapPin, Calendar, User, Briefcase, Loader2, Flag, Building2, CheckCircle2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useQuery } from "react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as DatePicker } from "@/components/ui/calendar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const profileSchema = z.object({
  phone: z.string().min(1, "Phone number is required"),
  gender: z.enum(["Male", "Female", "Other"], { required_error: "Select gender" }),
  country: z.string().min(1, "Country is required"),
  currentAddress: z.string().min(1, "Current address is required"),
  marriageStatus: z.enum(["Single", "Married", "Divorced", "Widowed"], { required_error: "Select marital status" }),
  employmentType: z.enum(["Permanent", "Contract", "Part-time", "Substitute"], { required_error: "Select employment type" }),
  department: z.string().min(1, "Department / Subject is required"),
  yearsOfExperience: z.string().min(1, "Years of experience is required"),
  joinedSavingsGroupDate: z.date({ required_error: "Join date is required" }),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

const REQUIRED_FIELDS: { key: keyof ProfileFormValues; label: string }[] = [
  { key: "phone", label: "Phone number" },
  { key: "gender", label: "Gender" },
  { key: "country", label: "Country" },
  { key: "currentAddress", label: "Current address" },
  { key: "marriageStatus", label: "Marital status" },
  { key: "employmentType", label: "Employment type" },
  { key: "department", label: "Department / Subject" },
  { key: "yearsOfExperience", label: "Years of experience" },
  { key: "joinedSavingsGroupDate", label: "Join date" },
];

export default function MyProfile() {
  const { user, loaderUser, portalMode } = useAuth();
  const [open, setOpen] = useState(false);

  // Determine if user is in member portal (leaders can switch to view their member profile)
  const isMember = portalMode === "member";
  const apiEndpoint = isMember ? `/members/${user?.id}` : `/teachers/${user?.id}`;
  const queryKey = isMember ? `member-profile-full-${user?.id}` : `teacher-profile-full-${user?.id}`;

  const {
    data: profileData,
    isLoading,
    refetch,
  } = useQuery(
    [queryKey, user?.id],
    async () => {
      const { data } = await api.get(apiEndpoint);
      return data.data;
    },
    {
      enabled: !!user?.id,
    }
  );

  const profile = profileData || user;

  const { data: savingsData, isLoading: savingsLoading } = useQuery(
    ["my-savings-history", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=50");
      return data.data || [];
    },
    { enabled: !!user?.id }
  );

  const missingFields = useMemo(() => {
    if (!profile) return [];
    return REQUIRED_FIELDS.filter((field) => {
      const value = (profile as any)[field.key];
      if (value === undefined || value === null || value === "") return true;
      if (field.key === "gender" && value === "Other") return true;
      return false;
    }).map((field) => field.label);
  }, [profile]);

  const needsCompletion = profile ? missingFields.length > 0 : false;
  const completionPercentage = Math.max(
    0,
    Math.round(((REQUIRED_FIELDS.length - missingFields.length) / REQUIRED_FIELDS.length) * 100)
  );
  const profileName =
    profile?.fullName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
    profile?.email?.split("@")[0] ||
    "Member profile";
  const profileInitials =
    `${profile?.firstName?.charAt(0) || ""}${profile?.lastName?.charAt(0) || ""}` ||
    profileName.slice(0, 2).toUpperCase();
  const membershipLabel = profile?.isGroupLeader ? "Leader" : "Member";
  const formattedJoinDate = profile?.joinedAt || profile?.joinedSavingsGroupDate
    ? new Date(profile?.joinedAt || profile?.joinedSavingsGroupDate).toLocaleDateString()
    : "Not recorded";
  const emptyValue = (value?: string | number | null, fallback = "Not provided") =>
    value === undefined || value === null || value === "" ? fallback : value;
  const formatMoney = (value?: number | string | null) =>
    `${Number(value || 0).toLocaleString()} FRW`;
  const summaryCards = [
    {
      title: "Total Savings",
      value: formatMoney(profile?.totalSavings),
      tone: "border-[#009A44]/20 bg-[#009A44]/5 text-[#009A44]",
    },
    {
      title: "Available Balance",
      value: formatMoney(profile?.currentSavings ?? profile?.totalSavings),
      tone: "border-blue-200 bg-blue-50/80 text-blue-700",
    },
    {
      title: "Loans & Fines",
      value: formatMoney(profile?.unpaidLoansAndFines),
      tone: "border-amber-200 bg-amber-50/80 text-amber-700",
    },
  ];
  const infoItems = [
    {
      label: "Full Name",
      value: profileName,
      icon: User,
    },
    {
      label: "Email",
      value: emptyValue(profile?.email),
      icon: Mail,
    },
    {
      label: "Phone Number",
      value: emptyValue(profile?.phone),
      icon: Phone,
    },
    {
      label: "Member ID",
      value: emptyValue(profile?.memberCode || profile?.teacherId, "Not assigned"),
      icon: Briefcase,
    },
    {
      label: "Address",
      value: emptyValue(profile?.currentAddress),
      icon: MapPin,
    },
    {
      label: "Joined Group",
      value: formattedJoinDate,
      icon: Calendar,
    },
  ];

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      phone: "",
      gender: "Male",
      country: "",
      currentAddress: "",
      marriageStatus: "Single",
      employmentType: "Contract",
      department: "",
      yearsOfExperience: "0",
      joinedSavingsGroupDate: new Date(),
    },
  });

  useEffect(() => {
    if (profile) {
      // Handle the supported member field names returned by the API.
      const joinedDate = profile.joinedSavingsGroupDate || profile.joinedAt;
      
      form.reset({
        phone: profile.phone || "",
        gender: profile.gender || "Male",
        country: profile.country || "",
        currentAddress: profile.currentAddress || "",
        marriageStatus: profile.marriageStatus || "Single",
        employmentType: profile.employmentType || "Contract",
        department: profile.department || "",
        yearsOfExperience:
          profile.yearsOfExperience !== undefined && profile.yearsOfExperience !== null
            ? String(profile.yearsOfExperience)
            : profile.yearsOfExperience !== undefined && profile.yearsOfExperience !== null
            ? String(profile.yearsOfExperience)
            : "0", // Default to "0" if not available (Member entity doesn't have this field)
        joinedSavingsGroupDate: joinedDate ? new Date(joinedDate) : new Date(),
      });
    }
  }, [profile, form]);

  const onSubmit = async (values: ProfileFormValues) => {
    try {
      // Use correct endpoint based on user type
      const updateEndpoint = isMember ? `/members/${user?.id}` : `/teachers/${user?.id}`;
      const updateMethod = isMember ? api.patch : api.put;
      
      // Prepare update payload - map joinedSavingsGroupDate to joinedAt for members
      const updatePayload: any = {
        phone: values.phone,
        gender: values.gender,
        country: values.country,
        currentAddress: values.currentAddress,
        marriageStatus: values.marriageStatus,
        employmentType: values.employmentType,
        department: values.department,
      };
      
      // Only include yearsOfExperience when the legacy profile shape supports it.
      if (!isMember) {
        updatePayload.yearsOfExperience = Number(values.yearsOfExperience);
      }
      
      // Map the join date to the field expected by each profile shape.
      if (isMember) {
        updatePayload.joinedAt = values.joinedSavingsGroupDate.toISOString().split("T")[0];
      } else {
        updatePayload.joinedSavingsGroupDate = values.joinedSavingsGroupDate.toISOString().split("T")[0];
      }
      
      await updateMethod(updateEndpoint, updatePayload);

      toast.success("Profile updated successfully");
      await Promise.all([refetch(), loaderUser()]);
      setOpen(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to update profile");
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="flex items-center justify-center h-80">
        <Loader2 className="h-10 w-10 animate-spin text-[#009A44]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-theme-text">My Profile</h1>
          <p className="text-muted-foreground mt-1">
            View and manage your personal information
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button onClick={() => setOpen(true)} className="bg-[#009A44] hover:bg-[#008238] text-white">
              {needsCompletion ? "Complete Your Profile" : "Update Profile"}
            </Button>
            {needsCompletion ? (
              <div className="flex items-center gap-1 text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
                <Flag className="h-4 w-4" />
                {missingFields.length} fields missing
              </div>
            ) : (
              <div className="flex items-center gap-1 text-sm text-[#009A44] bg-green-50 border border-green-200 rounded-full px-3 py-1">
                <CheckCircle2 className="h-4 w-4" />
                Profile complete
              </div>
            )}
          </div>
        </div>

        <Card className="overflow-hidden border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="grid gap-0 lg:grid-cols-[1.25fr_0.75fr]">
              <div className="bg-gradient-to-br from-white via-[#009A44]/5 to-[#009A44]/10 px-6 py-6 sm:px-8">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                  <Avatar className="h-24 w-24 border-4 border-white shadow-sm">
                    <AvatarImage src={profile?.profilePicture || getDefaultAvatar(profile?.id || profileName)} alt={profileName} />
                    <AvatarFallback className="bg-[#009A44] text-white text-3xl font-semibold">
                      {profileInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className="border-0 bg-[#009A44] text-white hover:bg-[#009A44]">
                        {membershipLabel}
                      </Badge>
                      <Badge variant="outline" className="border-[#009A44]/30 bg-white/80 text-slate-700">
                        {emptyValue(profile?.memberCode || profile?.teacherId, "No Member ID")}
                      </Badge>
                    </div>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-900">
                      {profileName}
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      {emptyValue(profile?.email, "No email address available")}
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-600">
                      <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-1.5">
                        <Phone className="h-4 w-4 text-[#009A44]" />
                        {emptyValue(profile?.phone)}
                      </span>
                      <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white/80 px-3 py-1.5">
                        <MapPin className="h-4 w-4 text-[#009A44]" />
                        {emptyValue(profile?.currentAddress)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-t border-slate-200 bg-slate-50 px-6 py-6 lg:border-l lg:border-t-0">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Profile Completion
                </p>
                <div className="mt-3 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-3xl font-semibold text-slate-900">
                      {completionPercentage}%
                    </p>
                    <p className="text-sm text-slate-500">
                      {needsCompletion
                        ? `${missingFields.length} field${missingFields.length > 1 ? "s" : ""} still missing`
                        : "All required details completed"}
                    </p>
                  </div>
                  {needsCompletion ? (
                    <Flag className="h-8 w-8 text-amber-500" />
                  ) : (
                    <CheckCircle2 className="h-8 w-8 text-[#009A44]" />
                  )}
                </div>
                <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200">
                  <div
                    className="h-full rounded-full transition-all bg-[#009A44]"
                    style={{ width: `${completionPercentage}%` }}
                  />
                </div>
                {needsCompletion && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {missingFields.slice(0, 3).map((field) => (
                      <Badge key={field} variant="outline" className="border-amber-300 bg-white text-slate-700">
                        {field}
                      </Badge>
                    ))}
                    {missingFields.length > 3 && (
                      <Badge variant="outline" className="border-amber-300 bg-white text-slate-700">
                        +{missingFields.length - 3} more
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
            <CardDescription>Your basic profile details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <Table className="w-full">
                <TableHeader className="bg-[#009A44]">
                  <TableRow className="border-b-0 hover:bg-transparent">
                    <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">
                      Field
                    </TableHead>
                    <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">
                      Details
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {infoItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <TableRow
                        key={item.label}
                        className={`border-b border-slate-100 transition-colors hover:bg-green-50/50 ${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                        }`}
                      >
                        <TableCell className="w-[34%] px-5 py-4 font-semibold text-slate-700">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#009A44]/10">
                              <Icon className="h-4 w-4 text-[#009A44]" />
                            </div>
                            <span className="text-[15px]">{item.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="px-5 py-4 text-[15px] font-medium text-slate-700">
                          <span className="block break-words">{item.value}</span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Savings Information */}
        <Card>
          <CardHeader>
            <CardTitle>Savings Summary</CardTitle>
            <CardDescription>Your contribution and savings overview</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {summaryCards.map((card) => (
                <div
                  key={card.title}
                  className={`rounded-xl border p-5 shadow-sm ${card.tone} border-theme-primary/20`}
                >
                  <p className="text-sm font-medium text-current/80">{card.title}</p>
                  <p className="mt-3 text-3xl font-semibold tracking-tight">{card.value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Savings History Table */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-[#009A44]" />
              Savings History
            </CardTitle>
            <CardDescription>Your recent contribution records</CardDescription>
          </CardHeader>
          <CardContent>
            {savingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#009A44]" />
              </div>
            ) : savingsData && savingsData.length > 0 ? (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <Table className="w-full">
                  <TableHeader className="bg-[#009A44]">
                    <TableRow className="border-b-0 hover:bg-transparent">
                      <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">#</TableHead>
                      <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">Amount</TableHead>
                      <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">Salary Month</TableHead>
                      <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">Date</TableHead>
                      <TableHead className="h-12 px-5 text-[12px] font-semibold uppercase tracking-[0.16em] text-white">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {savingsData.map((c: any, idx: number) => {
                      const salaryMonth = c.salaryMonth ? new Date(c.salaryMonth) : null;
                      const monthLabel = salaryMonth
                        ? `${salaryMonth.getFullYear()}-${String(salaryMonth.getMonth() + 1).padStart(2, "0")}`
                        : "N/A";
                      const dateLabel = c.contributionDate
                        ? new Date(c.contributionDate).toLocaleDateString()
                        : "N/A";
                      return (
                        <TableRow
                          key={c.id}
                          className={`border-b border-slate-100 transition-colors hover:bg-green-50/50 ${
                            idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                          }`}
                        >
                          <TableCell className="px-5 py-3.5 text-sm text-slate-500">{idx + 1}</TableCell>
                          <TableCell className="px-5 py-3.5 text-sm font-semibold text-[#009A44]">
                            {Number(c.amount || 0).toLocaleString()} FRW
                          </TableCell>
                          <TableCell className="px-5 py-3.5 text-sm text-slate-700">{monthLabel}</TableCell>
                          <TableCell className="px-5 py-3.5 text-sm text-slate-500">{dateLabel}</TableCell>
                          <TableCell className="px-5 py-3.5">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                              c.status === "Verified"
                                ? "bg-green-100 text-green-700"
                                : c.status === "Pending"
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-slate-100 text-slate-600"
                            }`}>
                              {c.status || "N/A"}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                No savings records found.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Group Information */}
        {profile?.teacherGroup && (
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Group Information</CardTitle>
              <CardDescription>Details about your savings group</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-[#009A44]/20 bg-[#009A44]/5 p-4">
                  <p className="text-sm text-slate-500">Group Name</p>
                  <p className="mt-1 font-semibold text-slate-900">{profile.teacherGroup.name}</p>
                </div>
                <div className="rounded-xl border border-[#009A44]/20 bg-[#009A44]/5 p-4">
                  <p className="text-sm text-slate-500">Location</p>
                  <p className="mt-1 font-semibold text-slate-900">
                    {emptyValue(profile.teacherGroup.location, "N/A")}
                  </p>
                </div>
                {profile.teacherGroup.enableShareSystem && (
                  <>
                    <div className="rounded-xl border border-[#009A44]/20 bg-[#009A44]/5 p-4">
                      <p className="text-sm text-slate-500">
                        {isMember ? "Contribution Unit Value" : "Share Value"}
                      </p>
                      <p className="mt-1 font-semibold text-slate-900">
                        {formatMoney(profile.teacherGroup.shareValue)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-[#009A44]/20 bg-[#009A44]/5 p-4">
                      <p className="text-sm text-slate-500">
                        {isMember ? "Contribution Range" : "Share Range"}
                      </p>
                      <p className="mt-1 font-semibold text-slate-900">
                        {profile.teacherGroup.minSharesPerMonth} -{" "}
                        {profile.teacherGroup.maxSharesPerMonth
                          ? `${profile.teacherGroup.maxSharesPerMonth} ${isMember ? "units" : "shares"}/month`
                          : "No maximum"}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <Dialog
          open={open}
          onOpenChange={(value) => {
            setOpen(value);
          }}
        >
          <DialogContent className="w-[98vw] sm:max-w-5xl lg:max-w-6xl max-h-[90vh] overflow-y-auto px-6 sm:px-10 pb-8">
            <DialogHeader>
              <DialogTitle>{needsCompletion ? "Complete Your Profile" : "Update Profile Information"}</DialogTitle>
              <DialogDescription>
                Provide accurate details so the group can communicate with you and approve your requests quickly.
              </DialogDescription>
            </DialogHeader>

            {missingFields.length > 0 && (
              <div className="flex flex-wrap gap-2 py-2">
                {missingFields.map((field) => (
                  <Badge key={field} variant="secondary" className="text-xs">
                    {field}
                  </Badge>
                ))}
              </div>
            )}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-4 sm:pb-2">
                <div className="grid gap-4 sm:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter phone number" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="gender"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Gender</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select gender" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Male">Male</SelectItem>
                            <SelectItem value="Female">Female</SelectItem>
                            <SelectItem value="Other">Prefer not to say</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Country</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter country" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="currentAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Current Address</FormLabel>
                        <FormControl>
                          <Input placeholder="Where do you live?" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="marriageStatus"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Marital Status</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Single">Single</SelectItem>
                            <SelectItem value="Married">Married</SelectItem>
                            <SelectItem value="Divorced">Divorced</SelectItem>
                            <SelectItem value="Widowed">Widowed</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="employmentType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employment Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Permanent">Permanent</SelectItem>
                            <SelectItem value="Contract">Contract</SelectItem>
                            <SelectItem value="Part-time">Part-time</SelectItem>
                            <SelectItem value="Substitute">Substitute</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="department"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Department / Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Mathematics, Science" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="yearsOfExperience"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Years of Experience</FormLabel>
                        <FormControl>
                          <Input type="number" min={0} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="joinedSavingsGroupDate"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Joined Savings Group Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn("justify-between text-left font-normal", !field.value && "text-muted-foreground")}
                              >
                                {field.value ? format(field.value, "PPP") : "Pick a date"}
                                <CalendarIcon className="ml-2 h-4 w-4 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <DatePicker
                              mode="single"
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={(date) => date > new Date()}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 pt-2">
                  {needsCompletion && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOpen(false)}
                      className="rounded-lg"
                    >
                      Close for now
                    </Button>
                  )}
                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="bg-[#009A44] hover:bg-[#008238] text-white px-8 py-2 text-sm font-semibold"
                  >
                    {form.formState.isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                    Update Profile
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
    </div>
  );
}

